import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    axios.get(`${API}/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <>
        <PageHero label="Insights" titleLines={["Article", "not found."]} accentIndex={1} />
        <section className="bg-paper py-24 md:py-32"><div className="container-px">
          <Link to="/blog" className="text-teal font-600">← Back to Insights</Link>
        </div></section>
      </>
    );
  }
  if (!article) return <div className="min-h-screen" />;

  const body = String(article.body || article.excerpt || "");
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  const safeHtml = isHtml ? DOMPurify.sanitize(body, { FORBID_TAGS: ["style", "form", "input", "textarea", "select", "button"], FORBID_ATTR: ["style"] }) : "";

  return (
    <>
      <Seo title={`${article.title} — AdX`} description={article.excerpt} />
      <PageHero label={article.tag || "Insights"} titleLines={[article.title]} />
      <section className="bg-paper py-24 md:py-32">
        <div className="container-px">
          <Reveal>
            <div className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.16em] text-ink/45">
              <span className="text-teal">{article.tag}</span><span>·</span><span>{article.read}</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            {article.cover_image && (
              <img src={article.cover_image} alt={article.title} data-testid="article-cover"
                className="mt-8 w-full max-h-[420px] object-cover rounded-xl border border-ink/10" />
            )}
            {isHtml ? (
              <div className="mt-8 article-html text-justify" data-testid="article-body" dangerouslySetInnerHTML={{ __html: safeHtml }} />
            ) : (
              <div className="mt-8 space-y-5 text-lg text-ink/70 leading-relaxed text-justify" data-testid="article-body">
                {body.split("\n").filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/blog" className="mt-14 inline-flex items-center gap-2 font-display font-600 text-ink hover:text-teal transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to Insights
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
